import { startCase } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EmployeeAvatar from "../../../components/EmployeeAvatar";
import Api from "../../../core/data/redux/utils/apiCall";
import { BASE_URLS, API_URLS } from "../../../core/data/redux/apis/urls";
import { processNocDownloadResponse } from "../../../utils/nocDownloadUtils";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  NOCOutjobRequest,
  setCurrentRequest,
  fetchNOCOutjobById,
} from "../../../core/data/redux/nocOutjobSlice";
import {
  ChatMessage,
  clearError,
  clearMessages,
  clearUploadError,
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage,
  uploadChatFiles,
} from "../../../core/data/redux/nocOutsideJobChatSlice";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
} from "../../../utils/nocChatUtils";
import { formatEmpIdForDisplay } from "../../../utils/empIdUtils";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../../utils/fileUtils";
import { all_routes } from "../../router/all_routes";

interface NOCOutsideJobDetailsProps { }

const NOCOutsideJobDetails = (props: NOCOutsideJobDetailsProps) => {
  const routes = all_routes;
  const { id } = useParams();
  const dispatch = useDispatch<any>();

  // Get data from Redux state
  const { requests, currentRequest, loading } = useSelector(
    (state: any) => state.nocOutjobState
  );
  const employeesState = useSelector((state: any) => state.employeesState);

  // Get chat data from Redux state
  const {
    messages,
    attachments,
    loading: chatLoading,
    unreadCount,
    error: chatError,
    uploading,
    uploadError,
  } = useSelector((state: any) => state.nocChat);

  const [applicationData, setApplicationData] =
    useState<NOCOutjobRequest | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [currentUserType, setCurrentUserType] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [isCurrentUserApprover, setIsCurrentUserApprover] =
    useState<boolean>(false);

  const handleDownloadPDF = async () => {
    if (!applicationData || isDownloading) return;
    try {
      setIsDownloading(true);
      const payload = {
        empId: applicationData.empID || applicationData.empId,
        type: "ZNOJ",
        referenceNumber: applicationData.referenceNumber,
      };

      const res = await Api.post(
        API_URLS.NOC.OUTSIDE_JOB_DOWNLOAD,
        payload
      );

      processNocDownloadResponse(res?.data);
    } catch (err: any) {
      console.error("Outside Job NOC Download Error:", err);
      toast.error(err?.message || "Failed to download NOC document.");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    dispatch(getMyProfile({}));
    const user = localStorage.getItem("user");
    const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
    setCurrentUserType(userJson?.role || userJson?.user_type || "");
    setCurrentUserId(userJson?.empId || userJson?.empID || "");
  }, [dispatch]);

  useEffect(() => {
    if (applicationData && currentUserId) {
      const appApprover = String(applicationData.approverId || (applicationData as any).approverUserID || "").replace(/^0+/, "");
      const myId = String(currentUserId).replace(/^0+/, "");
      const isApprover = appApprover === myId || (applicationData.approverId === currentUserId);
      setIsCurrentUserApprover(isApprover);
    }
  }, [applicationData, currentUserId]);

  // Fetch messages and unread count as soon as ID is available
  useEffect(() => {
    const requestId = id ? parseInt(id) : applicationData?.id;
    if (requestId && !isNaN(requestId)) {
      dispatch(fetchMessages({ nocRequestId: requestId }));
      dispatch(fetchUnreadCount(requestId));
    }
  }, [id, applicationData?.id, dispatch]);

  // Load data from Redux state & fetch by ID from API (noc-outjob-request-by-id)
  useEffect(() => {
    if (id) {
      const numId = parseInt(id);
      dispatch(fetchNOCOutjobById(numId))
        .unwrap()
        .then((res: any) => {
          if (res) setApplicationData(res);
        })
        .catch(() => {
          const found = requests.find((req: NOCOutjobRequest) => req.id === numId);
          if (found) setApplicationData(found);
        });
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (currentRequest && currentRequest.id === parseInt(id || "0")) {
      setApplicationData(currentRequest);
    }
  }, [currentRequest, id]);

  // Helper functions
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getDarStatusLabel = (code: string | undefined | null) => {
    if (!code) return "-";
    const status = String(code).trim().toUpperCase();
    const mapping: { [key: string]: string } = {
      N: "No D&AR contemplated against the employee",
      P: "D&AR Pending/ Deemed Suspension /Suspension/ Court Case Pending",
      M: "D&AR completed; Minor penalty has been imposed",
      J: "D&AR completed; Major penalty has been imposed",
    };
    return mapping[status] || code;
  };

  // Helper function to convert numbers to ordinal format
  const getOrdinalNumber = (num: string | number | undefined) => {
    if (!num) return "-";
    const n = typeof num === "string" ? parseInt(num) : num;
    if (isNaN(n)) return num;

    const suffixes = ["th", "st", "nd", "rd"];
    const remainder = n % 100;

    if (remainder >= 11 && remainder <= 13) {
      return n + "th";
    }

    const lastDigit = n % 10;
    const suffix = suffixes[lastDigit] || suffixes[0];
    return n + suffix;
  };

  // Helper function to convert y/n to yes/no
  const formatYesNo = (value: string | undefined) => {
    if (!value) return "-";
    const lowerValue = value.toLowerCase();
    if (lowerValue === "y" || lowerValue === "yes") return "Yes";
    if (lowerValue === "n" || lowerValue === "no") return "No";
    return value;
  };

  const getStatusBadgeClass = (status: string | undefined) => {
    if (!status) return "badge-secondary";
    switch (status.toLowerCase()) {
      case "pending":
      case "submitted":
        return "badge-warning";
      case "approved":
        return "badge-success";
      case "rejected":
        return "badge-danger";
      case "in_progress":
      case "under_review":
        return "badge-info";
      case "draft":
        return "badge-secondary";
      default:
        return "badge-secondary";
    }
  };

  const getNatureBadgeClass = (natureTitle: string | undefined) => {
    if (!natureTitle) return "bg-secondary-transparent text-secondary";

    switch (natureTitle.toLowerCase()) {
      case "government service":
        return "bg-primary-transparent text-primary";
      case "private/corporate service":
        return "bg-success-transparent text-success";
      case "consultancy":
        return "bg-info-transparent text-info";
      case "teaching/academic":
        return "bg-warning-transparent text-warning";
      case "business/entrepreneurship":
        return "bg-danger-transparent text-danger";
      default:
        return "bg-secondary-transparent text-secondary";
    }
  };

  // Get the display value for nature
  const getNatureDisplay = () => {
    if (!applicationData) return "-";

    const natureTitle =
      applicationData.natureOfOutsideJob || applicationData.natureOfOutside;

    if (!natureTitle) return "-";

    // If it's a number/ID and we have natureDetails, use the title
    if (
      /^\d+$/.test(natureTitle) &&
      applicationData.natureDetails?.natureTitle
    ) {
      return applicationData.natureDetails.natureTitle;
    }

    // If it's already a readable title, return it
    if (natureTitle && !/^\d+$/.test(natureTitle)) {
      return natureTitle;
    }

    // Fallback to raw value
    return natureTitle;
  };

  const getRejectionReasonDisplay = (reasonId: string | undefined | null) => {
    if (!reasonId || reasonId.toString().trim() === "0") return "-";
    const id = reasonId.toString().trim();
    const reasonsMap: Record<string, string> = {
      "1": "Incomplete details or documentation",
      "2": "Not eligible as per DMRC policy",
      "3": "Under D&AR/Vigilance proceedings",
      "4": "Other administrative reason"
    };
    return reasonsMap[id] || id;
  };

  const [showChat, setShowChat] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );

  // Helper to determine if a message is sent by current logged in user
  const isMyMessage = (msg: ChatMessage): boolean => {
    const userStr = localStorage.getItem("user");
    const userObj = userStr && userStr !== "undefined" ? JSON.parse(userStr) : {};
    const myEmpId = String(userObj?.empId || currentUserId || "").trim().replace(/^0+/, "");
    const senderEmpId = String(msg.senderEmpId || "").trim().replace(/^0+/, "");
    if (myEmpId && senderEmpId) {
      return myEmpId === senderEmpId;
    }
    const isApprover =
      userObj?.role === "APPROVER" ||
      userObj?.user_type === "APPROVER" ||
      isCurrentUserApprover ||
      Boolean(userObj?.approver_level);
    if (isApprover) {
      return msg.senderType === "approver" || msg.senderType === "admin";
    }
    return msg.senderType === "employee";
  };

  // Auto-scroll to bottom of chat when new messages arrive or when chat is opened
  useEffect(() => {
    if (showChat) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messages, showChat]);

  // Document request states
  const [showDocumentTypeDialog, setShowDocumentTypeDialog] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [pendingMessage, setPendingMessage] = useState<string>("");

  // Function to detect if message is requesting a document
  const isDocumentRequest = (message: string): boolean => {
    const documentKeywords = [
      "document",
      "documents",
      "file",
      "files",
      "upload",
      "attach",
      "attachment",
      "send",
      "provide",
      "submit",
      "certificate",
      "proof",
      "evidence",
      "scan",
      "copy",
      "copies",
    ];

    const lowerMessage = message.toLowerCase();
    return documentKeywords.some((keyword) => lowerMessage.includes(keyword));
  };

  // Helper function to get the last requested document type from messages
  const getLastRequestedDocumentType = (): string | undefined => {
    // Find the most recent message from approver that has documentType
    const approverMessages = messages
      .filter(
        (msg: ChatMessage) => msg.senderType === "approver" && msg.documentType
      )
      .reverse(); // Get most recent first

    if (approverMessages.length > 0) {
      return approverMessages[0].documentType;
    }

    return undefined;
  };

  // Helper function to convert document type to typeOfAttachedDoc format
  const getTypeOfAttachedDoc = (documentType?: string): string => {
    if (!documentType) return "E"; // Default to 'E' (Others/Education)

    switch (documentType.toLowerCase()) {
      case "education":
        return "E";
      case "advertisement":
        return "A";
      case "others":
      default:
        return "E";
    }
  };

  // Auto-refresh messages periodically and faster when chat is open
  useEffect(() => {
    const reqId = id ? parseInt(id) : applicationData?.id;
    if (!reqId || isNaN(reqId)) return;

    const intervalTime = showChat ? 5000 : 20000;
    const interval = setInterval(() => {
      dispatch(fetchMessages({ nocRequestId: reqId }));
      dispatch(fetchUnreadCount(reqId));
    }, intervalTime);

    return () => clearInterval(interval);
  }, [id, applicationData?.id, showChat, dispatch]);

  const handleSendMessage = async () => {
    const trimmedMsg = newMessage.trim();

    // Condition matching mobile app: if attachment is selected, message text is required
    if (selectedFiles.length > 0 && !trimmedMsg) {
      toast.warning("Please type a message with your attachment before sending.");
      return;
    }

    if (!trimmedMsg && selectedFiles.length === 0) {
      return;
    }

    if (!applicationData?.id) {
      return;
    }

    // Check if message contains document request keywords
    if (isDocumentRequest(trimmedMsg) && !selectedDocumentType) {
      setPendingMessage(trimmedMsg);
      setShowDocumentTypeDialog(true);
      return;
    }

    try {
      let uploadedDocs: any[] = [];
      const requestedDocType = getLastRequestedDocumentType();

      // If files are selected, upload them first
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(
            uploadResult.payload,
            selectedFiles,
            selectedDocumentType || requestedDocType
          );
        } else {
          toast.error("Failed to upload attached files. Please try again.");
          return;
        }
      }

      const attNames = uploadedDocs.map((d: any) => d.fileName).join(", ");
      const finalMessage = uploadedDocs.length > 0
        ? (trimmedMsg ? `${trimmedMsg}\n📎 ${attNames}` : `📎 ${attNames}`)
        : trimmedMsg;

      await dispatch(
        sendMessage({
          nocRequestId: applicationData.id,
          message: finalMessage,
          attachments: uploadedDocs,
          documents: uploadedDocs,
          documentType: selectedDocumentType || (uploadedDocs.length > 0 ? requestedDocType : undefined),
        })
      );

      setNewMessage("");
      setSelectedFiles([]);
      setSelectedDocumentType("");
      setPendingMessage("");

      // Refresh messages after sending
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      if (uploadedDocs.length > 0) {
        dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "OUTSIDE-JOB" }));
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error sending message. Please try again.");
    }
  };

  const handleDocumentTypeSelection = async () => {
    if (!selectedDocumentType) {
      alert("Please select a document type");
      return;
    }

    setShowDocumentTypeDialog(false);

    if (pendingMessage.trim() && applicationData?.id) {
      try {
        let uploadedDocs: any[] = [];
        if (selectedFiles.length > 0) {
          const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
          if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
            uploadedDocs = prepareUploadedAttachments(
              uploadResult.payload,
              selectedFiles,
              selectedDocumentType
            );
          }
        }

        await dispatch(
          sendMessage({
            nocRequestId: applicationData.id,
            message: pendingMessage,
            attachments: uploadedDocs,
            documentType: selectedDocumentType,
          })
        );

        setNewMessage("");
        setSelectedFiles([]);
        setSelectedDocumentType("");
        setPendingMessage("");

        // Refresh messages after sending
        dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      } catch (error) {
        console.error("Error sending message:", error);
      }
    }
  };

  const handleCancelDocumentType = () => {
    setShowDocumentTypeDialog(false);
    setSelectedDocumentType("");
    setPendingMessage("");
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        // Validate file type
        if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
          errors.push(
            `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(
              ", "
            )}`
          );
          return;
        }

        // Validate file size
        if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
          errors.push(
            `${file.name}: File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
          );
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        alert("File validation errors:\n" + errors.join("\n"));
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }

    // Clear the input value to allow selecting the same file again
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleChatToggle = () => {
    setShowChat(!showChat);
    if (!showChat && applicationData?.id) {
      dispatch(fetchMessages({ nocRequestId: applicationData.id }));
      dispatch(fetchChatAttachments({ nocRequestId: applicationData.id, nocType: "OUTSIDE_JOB" }));
    } else {
      dispatch(clearMessages());
      // Clear file states when closing chat
      setSelectedFiles([]);
      setUploadingFiles(new Set());
    }
  };

  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title: string;
    isImage: boolean;
  } | null>(null);

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleAttachmentClick = async (rawAttachment: any) => {
    if (!rawAttachment) return;
    await openChatAttachment(rawAttachment, applicationData?.id, "OUTSIDE_JOB", dispatch, attachments);
  };

  const handleDocumentClick = async (docParam: any) => {
    if (!docParam) {
      alert("Document file information is missing.");
      return;
    }

    // Check if docParam has embedded base64 content
    if (typeof docParam === "object" && docParam.content && typeof docParam.content === "string" && docParam.content.trim() !== "") {
      const contentStr = docParam.content.trim();
      const base64Url = contentStr.startsWith("data:")
        ? contentStr
        : `data:application/pdf;base64,${contentStr}`;
      window.open(base64Url, "_blank");
      return;
    }

    const fileName = typeof docParam === "string"
      ? docParam
      : (docParam.fileName || docParam.awsKey || docParam.UUID || docParam.docID);

    if (!fileName || fileName.trim() === "") {
      alert("Document key is missing. Unable to open file.");
      return;
    }

    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      window.open(fileName, "_blank");
      return;
    }

    // Synchronously open a new tab to bypass browser popup blockers
    const popup = window.open("", "_blank");
    if (popup) {
      popup.document.title = "Loading document...";
      popup.document.body.innerHTML = `
        <div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#555;">
          <div style="text-align:center;">
            <div style="font-size:18px;font-weight:600;margin-bottom:8px;">Loading document...</div>
            <div style="font-size:14px;color:#888;">Please wait while your file is prepared.</div>
          </div>
        </div>
      `;
    }

    try {
      const candidateKeys: string[] = [];
      if (fileName.includes("/")) {
        candidateKeys.push(fileName);
        const fileBasename = fileName.split("/").pop() || "";
        if (fileBasename) {
          candidateKeys.push(`noc_doc/${fileBasename}`);
          candidateKeys.push(fileBasename);
        }
      } else {
        candidateKeys.push(`noc_doc/${fileName}`);
        candidateKeys.push(fileName);
        candidateKeys.push(`NOC_OUTJOB/${fileName}`);
      }

      // 4. Request Presigned URL from S3 bucket endpoint
      for (const key of candidateKeys) {
        try {
          const endPoint = `${BASE_URLS.BASE_URL}aws-bucket/get-presigned-url`;
          const res: any = await Api.post(endPoint, { key });
          const resData = res?.data;
          let pUrl = "";
          if (typeof resData === "string") {
            pUrl = resData;
          } else if (typeof resData?.data === "string") {
            pUrl = resData.data;
          } else if (typeof resData?.data?.url === "string") {
            pUrl = resData.data.url;
          } else if (typeof resData?.url === "string") {
            pUrl = resData.url;
          } else if (typeof resData?.presignedUrl === "string") {
            pUrl = resData.presignedUrl;
          }

          if (pUrl && (pUrl.startsWith("http://") || pUrl.startsWith("https://"))) {
            if (popup && !popup.closed) {
              popup.location.href = pUrl;
            } else {
              window.open(pUrl, "_blank");
            }
            return;
          }
        } catch (keyErr) {
          console.warn(`Failed trying S3 key "${key}":`, keyErr);
        }
      }

      // 5. Fallback: try docID endpoint if presigned URL is unavailable
      const docID = typeof docParam === "object" ? (docParam.docID || docParam.docId || docParam.id) : null;
      if (docID) {
        try {
          const docRes: any = await Api.post(BASE_URLS.BASE_URL + "noc/noc-outjob-doc-by-docid", { docId: docID });
          const docData = docRes?.data?.d?.results?.[0] || docRes?.data?.data?.d?.results?.[0] || docRes?.data?.data;
          const base64Content = docData?.Content || docData?.FileContent || docData?.content;
          if (base64Content) {
            const cleanBase64 = base64Content.replace(/^data:.*?,/, "");
            const byteCharacters = atob(cleanBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(pdfBlob);
            if (popup && !popup.closed) {
              popup.location.href = blobUrl;
            } else {
              window.open(blobUrl, "_blank");
            }
            return;
          }
        } catch (apiErr) {
          console.error("Error fetching doc by docID:", apiErr);
        }
      }

      if (popup && !popup.closed) {
        popup.close();
      }
      alert("Unable to open file. Please try again.");
    } catch (error) {
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error("Error opening document:", error);
      alert("Unable to open file. Please try again.");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!applicationData) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            NOC request not found. Please go back to the main page and try
            again.
          </div>
          <Link to={routes.noc.outsideJob} className="btn btn-primary">
            <i className="ti ti-arrow-left me-2"></i>
            Back to NOC List
          </Link>
        </div>
      </div>
    );
  }

  const natureDisplay = getNatureDisplay();
  const natureBadgeClass = getNatureBadgeClass(natureDisplay);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.outsideJob}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                NOC for Outside Job - {applicationData.referenceNumber}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.outsideJob}>Outside Job</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Application Details
                </li>
              </ol>
            </nav>
          </div>

          {String((applicationData as any)?.requestStatus || "").toUpperCase() === "D" && (
            <button
              className="btn btn-success ms-auto"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
            >
              <i className="ti ti-download me-2"></i>
              {isDownloading ? "Downloading..." : "Download Digital NOC"}
            </button>
          )}
        </div>



        {/* Enhanced Chat Section - Moved to top */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Communication</h5>
              {/* 
                Chat Access Logic (matches Mobile):
                - Approver / Admin / HR: can always start/view chat
                - Employee: can view/respond if messages exist (or if approver sent message)
              */}
              {(() => {
                const isApproverOrAdmin =
                  isCurrentUserApprover ||
                  (currentUserType && currentUserType.toUpperCase() !== "EMPLOYEE");
                const hasApproverMessage =
                  messages &&
                  messages.length > 0 &&
                  messages.some(
                    (m: any) =>
                      String(m.senderEmpId || "").replace(/^0+/, "") ===
                      String(applicationData?.approverId || "").replace(/^0+/, "") ||
                      m.senderType === "approver" ||
                      m.senderType === "admin"
                  );
                const hasAnyMessage = messages && messages.length > 0;
                const canChat = isApproverOrAdmin || hasApproverMessage || hasAnyMessage;

                if (!canChat) {
                  return (
                    <div className="text-muted small">
                      <i className="ti ti-info-circle me-1"></i>
                      Chat will be available once the approver initiates communication
                    </div>
                  );
                }

                return (
                  <button
                    className="btn btn-primary btn-sm position-relative"
                    onClick={handleChatToggle}
                  >
                    <i className="ti ti-message me-1"></i>
                    {showChat
                      ? "Close Chat"
                      : messages.length > 0
                        ? "View Chat"
                        : "Start Chat"}
                    {unreadCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>

        {(() => {
          const appData = applicationData as any;
          const profileUser = employeesState?.data?.data || employeesState?.data;
          const profileDetails = profileUser?.details || profileUser || {};
          const storedUser = (() => {
            try {
              const u = localStorage.getItem("user");
              return u && u !== "undefined" ? JSON.parse(u) : {};
            } catch {
              return {};
            }
          })();
          const storedDetails = storedUser?.details || storedUser || {};

          const empDetails =
            appData?.employee?.details ||
            appData?.employee ||
            appData?.employeeDetails?.details ||
            appData?.employeeDetails ||
            {};

          const sapInfo =
            empDetails?.sap_department_designation_wings ||
            profileDetails?.sap_department_designation_wings ||
            storedDetails?.sap_department_designation_wings ||
            {};

          const formatCategory = (cat: any) => {
            if (!cat) return "-";
            const str = String(cat).trim().toUpperCase();
            if (str === "G" || str === "GEN" || str === "GENERAL") return "GENERAL";
            if (str === "O" || str === "OBC") return "OBC";
            if (str === "SC") return "SC";
            if (str === "ST") return "ST";
            if (str === "EWS") return "EWS";
            return cat;
          };

          const empName =
            (empDetails.firstname || profileDetails.firstname || storedDetails.firstname)
              ? `${empDetails.firstname || profileDetails.firstname || storedDetails.firstname} ${empDetails.lsastname || empDetails.lastname || profileDetails.lsastname || profileDetails.lastname || storedDetails.lsastname || storedDetails.lastname || ""}`.trim()
              : empDetails.fullName || profileDetails.fullName || storedDetails.fullName || appData.employeeName || "-";

          const designation =
            sapInfo.designation_name ||
            empDetails.designation ||
            profileDetails.designation ||
            storedDetails.designation ||
            empDetails.designation_details?.designationName ||
            profileDetails.designation_details?.designationName ||
            storedDetails.designation_details?.designationName ||
            appData.designation ||
            "-";

          const dateOfJoining =
            empDetails.date_of_joining ||
            profileDetails.date_of_joining ||
            storedDetails.date_of_joining ||
            empDetails.doj ||
            profileDetails.doj ||
            storedDetails.doj ||
            appData.dateOfJoining ||
            appData.doj;

          const employmentStatus =
            empDetails.employmentType ||
            profileDetails.employmentType ||
            storedDetails.employmentType ||
            empDetails.employment_status ||
            profileDetails.employment_status ||
            storedDetails.employment_status ||
            empDetails.employeeStatus ||
            profileDetails.employeeStatus ||
            storedDetails.employeeStatus ||
            appData.employeeStatus ||
            appData.employmentType ||
            "Permanent";

          const department =
            appData.departmentName ||
            sapInfo.department_name ||
            empDetails.departmentName ||
            empDetails.department ||
            profileDetails.departmentName ||
            profileDetails.department ||
            storedDetails.departmentName ||
            storedDetails.department ||
            "-";

          const contactNumber =
            appData.contact_info ||
            appData.contactNumber ||
            empDetails.phone ||
            empDetails.contact_info ||
            profileDetails.phone ||
            profileDetails.contact_info ||
            storedDetails.phone ||
            storedDetails.contact_info ||
            "-";

          const emailId =
            appData.emailId ||
            empDetails.email ||
            profileDetails.email ||
            storedDetails.email ||
            "-";

          const dateOfBirth =
            appData.dateOfBirth ||
            empDetails.birthdate ||
            profileDetails.birthdate ||
            storedDetails.birthdate ||
            empDetails.dob ||
            profileDetails.dob;

          const category = formatCategory(
            appData.employeeDetails?.category ||
            empDetails.category ||
            profileDetails.category ||
            storedDetails.category ||
            appData.category
          );

          const lwp =
            empDetails.lwp ||
            profileDetails.lwp ||
            storedDetails.lwp ||
            appData.lwp ||
            "00000";

          const calculateAge = (dateStr: string | undefined): string => {
            if (!dateStr || dateStr === "-" || dateStr === "N/A") return "";
            let birthDate;
            if (dateStr.includes("-") && dateStr.split("-").length === 3) {
              const parts = dateStr.split("-");
              if (parts[0].length === 2) {
                birthDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              } else {
                birthDate = new Date(dateStr);
              }
            } else {
              birthDate = new Date(dateStr);
            }
            if (isNaN(birthDate.getTime())) return "";
            const today = new Date();
            let years = today.getFullYear() - birthDate.getFullYear();
            let months = today.getMonth() - birthDate.getMonth();
            let days = today.getDate() - birthDate.getDate();
            if (days < 0) {
              months--;
              const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
              days += lastMonth.getDate();
            }
            if (months < 0) {
              years--;
              months += 12;
            }
            return `${years}y, ${months}m, ${days}d`;
          };

          const age =
            appData.age ||
            empDetails.age ||
            profileDetails.age ||
            storedDetails.age ||
            calculateAge(dateOfBirth) ||
            "-";

          return (
            <>
              {/* 1. Application Details */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-briefcase me-2 text-primary"></i>
                    Application Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Ref No.</small>
                        <div className="fw-bold text-primary fs-15">{appData.referenceNumber || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Employee ID</small>
                        <div className="fw-bold text-dark fs-15">{formatEmpIdForDisplay(appData.empID || appData.empNo) || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Currently With</small>
                        <div className="fw-bold text-dark fs-15">{formatEmpIdForDisplay(appData.approverId) || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Creation Date</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(appData.requestCreationDat || appData.created_at)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Organisation</small>
                        <div className="fw-bold text-dark fs-15">{appData.organisationInWhich || appData.organisation || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">D&AR Status</small>
                        <div className="fw-bold text-dark fs-15">{getDarStatusLabel(appData.dnARStatus || appData.dandARStatus)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Remarks by Controlling</small>
                        <div className="fw-bold text-dark fs-15">{appData.Remarks || appData.hRemarks || appData.remarks || appData.Remarks1 || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Final D&AR / Vigilance Clearance</small>
                        <div className="fw-bold text-dark fs-15">{appData.finalDandARVigilance || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Reason for Rejection (in case of rejection)</small>
                        <div className="fw-bold text-dark fs-15">{getRejectionReasonDisplay(appData.ReasonForRejection || appData.reasonForRejection || appData.selectedRejectionReason)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Employee Details */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-user me-2 text-primary"></i>
                    Employee Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Name</small>
                        <div className="fw-bold text-dark fs-15">{empName}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Designation</small>
                        <div className="fw-bold text-dark fs-15">{designation}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Date of Joining in DMRC</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(dateOfJoining)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Employment Status</small>
                        <div className="fw-bold text-dark fs-15">{employmentStatus}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Department</small>
                        <div className="fw-bold text-dark fs-15">{department}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Contact Number</small>
                        <div className="fw-bold text-dark fs-15">{contactNumber}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Email ID</small>
                        <div className="fw-bold text-dark fs-15">{emailId}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Date of Birth</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(dateOfBirth)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Category</small>
                        <div className="fw-bold text-dark fs-15">{category}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">LWP</small>
                        <div className="fw-bold text-dark fs-15">{lwp}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Age</small>
                        <div className="fw-bold text-dark fs-15">{age}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Declaration */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-shield-check me-2 text-primary"></i>
                    Declaration
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Whether any penalty has been imposed in last 10 years by D&AR</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.dandARPendingOrNot || appData.dandARStatus;
                            return val === "Y" || val === "Yes" ? "Yes" : val === "N" || val === "No" ? "No" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Whether any penalty has been imposed in last 10 years by Vigilance</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.vigilancePending;
                            return val === "Y" || val === "Yes" ? "Yes" : val === "N" || val === "No" ? "No" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Whether probation is confirmed in DMRC</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.whetherProbation;
                            return val === "N" || val === "No" ? "No" : val === "Y" || val === "Yes" ? "Yes" : val === "0" || val === "Not Applicable" ? "Not Applicable" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">
                          {appData.whetherProbation === "0" || appData.whetherProbation === "Not Applicable"
                            ? "Whether two years service completed after Absorption"
                            : "Whether after probation confirmation, one year regular service is complete"}
                        </span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.whetherProbationConfirmed || appData.threeyearsRegularServ;
                            return val === "N" || val === "No" ? "No" : val === "Y" || val === "Yes" ? "Yes" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                        <span className="text-muted fw-medium">Has the employee been imparted training abroad by DMRC</span>
                        <span className="fw-bold text-dark fs-15">
                          {(() => {
                            const val = appData.trainedAbroadByDM;
                            return val === "N" || val === "No" ? "No" : val === "Y" || val === "Yes" ? "Yes" : "-";
                          })()}
                        </span>
                      </div>
                    </div>
                    {(appData.trainedAbroadByDM === "Y" || appData.trainedAbroadByDM === "Yes") && (
                      <>
                        <div className="col-md-6">
                          <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                            <span className="text-muted fw-medium">Place of Training Abroad</span>
                            <span className="fw-bold text-dark fs-15">
                              {appData.placeAbroadTrain || "-"}
                            </span>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                            <span className="text-muted fw-medium">Year of Training Abroad</span>
                            <span className="fw-bold text-dark fs-15">
                              {appData.yearOfAbroadTrain || "-"}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Advertisement Details */}
              <div className="card mt-4">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0 text-dark fw-bold">
                    <i className="ti ti-file-description me-2 text-primary"></i>
                    Advertisement Details
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">No. of Application Forwarded in the Year</small>
                        <div className="fw-bold text-dark fs-15">
                          {getOrdinalNumber(appData.appNoInYear || appData.noOfApplicaForwYears || "1")}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Place of Posting</small>
                        <div className="fw-bold text-dark fs-15">{appData.placeofPosting || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Nature of Outside Job</small>
                        <div className="fw-bold text-dark fs-15">
                          {appData.natureDetails?.natureTitle || appData.natureOfOutsideJob || appData.natureOfOutside || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Name of Post Applied For</small>
                        <div className="fw-bold text-primary fs-15">
                          {appData.postAppliedFor || appData.postAppliedfor || "-"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Newspaper / Website</small>
                        <div className="fw-bold text-dark fs-15">{appData.newspaperWebsite || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Advertisement No.</small>
                        <div className="fw-bold text-dark fs-15">{appData.advertisementNo || appData.advertisementNo1 || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Advertisement Date</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(appData.advertisementDate || appData.advertisementDate1)}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Name of the Organization in which Applied</small>
                        <div className="fw-bold text-dark fs-15">{appData.organisation || appData.organisationInWhich || "-"}</div>
                      </div>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <div className="p-3 bg-light rounded border h-100">
                        <small className="text-muted d-block uppercase fw-medium mb-1">Last Date of Receipt of Application</small>
                        <div className="fw-bold text-dark fs-15">{formatDate(appData.lastDateOfApplica || appData.lastDateReceipt)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Education Details */}
              {appData.education && appData.education.length > 0 && (
                <div className="card mt-4">
                  <div className="card-header bg-light">
                    <h5 className="card-title mb-0 text-dark fw-bold">
                      <i className="ti ti-school me-2 text-primary"></i>
                      Education Details ({appData.education.length})
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="d-flex flex-column gap-3">
                      {appData.education.map((edu: any, index: number) => (
                        <div key={index} className="p-3 bg-light rounded border">
                          <h6 className="text-primary fw-bold mb-3">
                            Education Details {index + 1}
                          </h6>
                          <div className="row g-3">
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Course Name</small>
                              <div className="fw-bold text-dark">{edu.courseName || "-"}</div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Date of Admission</small>
                              <div className="fw-bold text-dark">{formatDate(edu.courseDateOfAdmission)}</div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Date of Passing</small>
                              <div className="fw-bold text-dark">{formatDate(edu.courseDateOfPassing)}</div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">When Qualification Acquired</small>
                              <div className="fw-bold text-dark">
                                {edu.qualificationAcquir === "A" ? "After Joining" : edu.qualificationAcquir === "B" ? "Before Joining" : "-"}
                              </div>
                            </div>
                            <div className="col-md-6 col-lg-4">
                              <small className="text-muted d-block uppercase fw-medium mb-1">Course Affiliated by UGC/AICTE</small>
                              <div className="fw-bold text-dark">{edu.isCourseRecog === "Y" ? "Yes" : "No"}</div>
                            </div>
                          </div>

                          {/* Attached edu_documents */}
                          {edu.edu_documents && edu.edu_documents.length > 0 && (
                            <div className="mt-3 pt-3 border-top">
                              <small className="text-muted d-block uppercase fw-medium mb-2">Education/Qualification Certificates</small>
                              <div className="d-flex flex-wrap gap-2">
                                {edu.edu_documents.map((doc: any, dIdx: number) => (
                                  <button
                                    key={dIdx}
                                    type="button"
                                    className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                                    onClick={() => handleDocumentClick(doc)}
                                    title="View Document"
                                    disabled={!doc.fileName && !doc.UUID && !doc.docID}
                                  >
                                    <i className="ti ti-file-text"></i>
                                    <span>{doc.fileName ? doc.fileName.split("/").pop() : `Certificate ${dIdx + 1}`}</span>
                                    <i className="ti ti-eye ms-1"></i>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* 6. Attachments Section */}
              {(() => {
                const mainDocs = Array.isArray(appData.documents) ? appData.documents : [];
                const eduDocs: any[] = [];
                if (Array.isArray(appData.education)) {
                  appData.education.forEach((edu: any) => {
                    if (Array.isArray(edu.edu_documents)) {
                      eduDocs.push(...edu.edu_documents);
                    }
                  });
                }
                const combined = [...mainDocs, ...eduDocs];
                const allNocDocuments = combined
                  .filter(
                    (doc: any, idx: number, self: any[]) =>
                      idx === self.findIndex((t: any) => (t.id && doc.id ? t.id === doc.id : t.fileName === doc.fileName))
                  )
                  .filter(
                    (doc: any) => (!doc.referenceNumber ? true : doc.referenceNumber.toString().length < 20)
                  );

                if (!allNocDocuments || allNocDocuments.length === 0) return null;

                return (
                  <div className="card mt-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0 text-dark fw-bold">
                        <i className="ti ti-files me-2 text-primary"></i>
                        Attachments ({allNocDocuments.length})
                      </h5>
                    </div>
                    <div className="card-body">
                      {/* Mandatory Attachments Info Card */}
                      <div className="alert alert-danger bg-danger-transparent border-danger mb-3 p-3">
                        <h6 className="text-danger mb-2">
                          <i className="ti ti-info-circle me-2"></i>
                          Mandatory Attachments
                        </h6>
                        <ul className="mb-0 ps-3 small text-muted">
                          <li>- Copy of advertisement indicating last date of application and post applied for must be attached.</li>
                          <li>- If, with the existing educational qualifications (as captured in SAP), the employee is not eligible for the post, uploading requisite educational qualification documents is mandatory, on basis of which the employee is eligible for the post that he/she intends to apply for.</li>
                          <li>- Documents to be uploaded - Final Marksheet/Certificate</li>
                        </ul>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">File Name</th>
                              <th scope="col">Document Type</th>
                              <th scope="col">Upload Date</th>
                              <th scope="col">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allNocDocuments.map((doc: any, index: number) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <i className="ti ti-file-text text-primary me-2"></i>
                                    {index + 1}
                                  </div>
                                </td>
                                <td>
                                  <div className="fw-medium">
                                    {doc.fileName ? doc.fileName.split("/").pop() : `File ${index + 1}`}
                                  </div>
                                </td>
                                <td>
                                  <span className={`badge ${doc.typeOfAttachedDoc === "E" || doc.typeOfAttachedDoc === "Qualification"
                                      ? "bg-success"
                                      : doc.typeOfAttachedDoc === "N" || doc.typeOfAttachedDoc === "NOC/Addition"
                                        ? "bg-info"
                                        : doc.typeOfAttachedDoc === "A" || doc.typeOfAttachedDoc === "advertising"
                                          ? "bg-dark"
                                          : "bg-secondary"
                                    }`}>
                                    {doc.typeOfAttachedDoc === "N" || doc.typeOfAttachedDoc === "NOC/Addition"
                                      ? "NOC or Addition Office Order"
                                      : doc.typeOfAttachedDoc === "E" || doc.typeOfAttachedDoc === "Qualification"
                                        ? "Education/Qualification Certificates"
                                        : doc.typeOfAttachedDoc === "A" || doc.typeOfAttachedDoc === "advertising"
                                          ? "Advertisement for Outside Job"
                                          : doc.typeOfAttachedDoc || "General"}
                                  </span>
                                </td>
                                <td>
                                  <span className="text-muted">
                                    {formatDate((doc as any).created_at)}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleDocumentClick(doc)}
                                    title="View/Download File"
                                    disabled={!doc.fileName && !doc.UUID && !doc.docID}
                                  >
                                    <i className="ti ti-eye me-1"></i>
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 7. Previous Remarks & Approval History Section */}
              {(() => {
                let rawList: any[] = [
                  ...(Array.isArray(appData?.remarks_data) && appData.remarks_data.length > 0
                    ? appData.remarks_data
                    : Array.isArray(appData?.history) && appData.history.length > 0
                    ? appData.history
                    : Array.isArray(appData?.work_history) && appData.work_history.length > 0
                    ? appData.work_history
                    : Array.isArray(appData?.remarksHistory) && appData.remarksHistory.length > 0
                    ? appData.remarksHistory
                    : [])
                ].map((item: any) => ({ ...item }));

                const applicantId = String(
                  appData?.empId ||
                  appData?.empID ||
                  appData?.employee?.empId ||
                  appData?.employee?.empID ||
                  ""
                ).replace(/^0+/, "");

                const hasEmployeeEntry = rawList.some((item: any) => {
                  const itemUserId = String(
                    item?.approverUserID || item?.userId || item?.empID || ""
                  ).replace(/^0+/, "");
                  return (
                    item?.usertype === "E" ||
                    item?.usertype === "Employee" ||
                    item?.approverName === "User" ||
                    (applicantId && itemUserId && itemUserId === applicantId)
                  );
                });

                const employeeRemarks = (() => {
                  const candidates = [
                    appData?.anyRemarks,
                    appData?.any_remarks,
                    appData?.employeeRemarks,
                    appData?.empRemarks,
                    appData?.remarks !== "X" ? appData?.remarks : null,
                    appData?.Remarks !== "X" ? appData?.Remarks : null,
                  ];
                  for (const cand of candidates) {
                    if (typeof cand === "string" && cand.trim() && cand.trim() !== "X" && cand.trim() !== "-" && cand.trim() !== "null") {
                      return cand.trim();
                    }
                  }
                  return "";
                })();

                const employeeTimestamp =
                  appData?.requestCreationDate ||
                  appData?.created_at ||
                  appData?.createdAt ||
                  appData?.sap_created_at ||
                  appData?.sapCreatedAt ||
                  appData?.appliedDate ||
                  null;

                const employeeName = (() => {
                  if (appData?.employee) {
                    const fname = appData.employee.firstname || appData.employee.firstName || "";
                    const lname = appData.employee.lsastname || appData.employee.lastname || appData.employee.lastName || "";
                    const full = `${fname} ${lname}`.trim();
                    if (full) return full;
                  }
                  return appData?.employeeName || appData?.empName || "Employee";
                })();

                const employeeUserId = appData?.empId || appData?.empID || appData?.employee?.empId || "";

                if (hasEmployeeEntry) {
                  rawList = rawList.map((item: any) => {
                    const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                    const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                    if (isEmp && !item?.remarks && employeeRemarks) {
                      return { ...item, remarks: employeeRemarks };
                    }
                    return { ...item };
                  });
                } else if (appData && (employeeUserId || employeeName !== "Employee" || appData?.id || appData?.referenceNumber)) {
                  rawList.push({
                    usertype: "Employee",
                    approverUserID: employeeUserId,
                    userId: employeeUserId,
                    empID: employeeUserId,
                    approverName: employeeName,
                    name: employeeName,
                    remarks: employeeRemarks,
                    createdAt: employeeTimestamp,
                    isEmployee: true,
                  });
                }

                if (rawList.length === 0) return null;

                return (
                  <div className="card mt-4">
                    <div className="card-header bg-light">
                      <h5 className="card-title mb-0 text-dark fw-bold">
                        <i className="ti ti-history me-2 text-primary"></i>
                        Remarks & Approval History
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="d-flex flex-column gap-3">
                        {rawList.map((item: any, index: number) => {
                          const itemUserId = String(item?.approverUserID || item?.userId || item?.empID || "").replace(/^0+/, "");
                          const isEmp = item?.usertype === "E" || item?.usertype === "Employee" || item?.approverName === "User" || (applicantId && itemUserId && itemUserId === applicantId);
                          return (
                            <div
                              key={index}
                              className={`p-3 bg-white rounded border border-start-4 ${isEmp ? "border-start-info" : "border-start-primary"} shadow-xs`}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="d-flex align-items-center gap-2">
                                  <div className={`avatar avatar-sm ${isEmp ? "bg-info-transparent text-info" : "bg-primary-transparent text-primary"} rounded-circle fw-bold`}>
                                    <i className={`${isEmp ? "ti ti-user" : "ti ti-user-check"} fs-16`}></i>
                                  </div>
                                  <div>
                                    <div className="d-flex align-items-center gap-2">
                                      <h6 className="mb-0 text-dark fw-bold">
                                        {item.approverName ||
                                          item.name ||
                                          item.history_approver?.name ||
                                          (isEmp ? "Employee" : "Approver")}
                                      </h6>
                                      <span className={`badge ${isEmp ? "bg-info-subtle text-info" : "bg-warning-subtle text-warning"} border text-xs`}>
                                        {isEmp ? "Employee" : "Approver"}
                                      </span>
                                    </div>
                                    <small className="text-muted">
                                      ID:{" "}
                                      {formatEmpIdForDisplay(
                                        item.approverUserID ||
                                        item.userId ||
                                        item.history_approver?.empId ||
                                        (isEmp ? employeeUserId : "")
                                      ) || "-"}
                                    </small>
                                  </div>
                                </div>
                                <span className="badge bg-light text-muted border">
                                  <i className="ti ti-calendar me-1"></i>
                                  {formatDate(
                                    item.createdAt ||
                                    item.created_at ||
                                    item.timeStamp
                                  )}
                                  {(item.createdAt || item.created_at || item.timeStamp) && !isNaN(new Date(item.createdAt || item.created_at || item.timeStamp).getTime())
                                    ? " " + new Date(item.createdAt || item.created_at || item.timeStamp).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                      hour12: true,
                                    }).toUpperCase()
                                    : ""}
                                </span>
                              </div>
                              <div className="mt-2 pt-2 border-top">
                                <p className="mb-0 text-dark fw-medium fs-14">
                                  {item.remarks ? (
                                    <span>
                                      <i className="ti ti-quote text-muted me-1"></i>
                                      {item.remarks}
                                    </span>
                                  ) : (
                                    <span className="text-muted fst-italic">No remarks</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 8. Declaration Section Footer */}
              <div className="card mt-4 bg-light border-0 shadow-xs">
                <div className="card-body">
                  <h6 className="fw-bold text-dark mb-2">
                    <i className="ti ti-file-certificate me-2 text-primary"></i>
                    Declaration
                  </h6>
                  <ul className="mb-0 ps-3 small text-muted">
                    <li>I hereby declare that the information furnished above is true to the best of my knowledge and belief.</li>
                    <li>I understand that if any of the above information is found to be false or incorrect at any stage, my application/NOC shall be liable to be cancelled without any notice.</li>
                    {(appData.natureOfOutsideJob === "Deputation" || appData.natureDetails?.natureTitle === "Deputation") && (
                      <li>Terms and conditions of deputation as applicable in DMRC.</li>
                    )}
                  </ul>
                </div>
              </div>

              {/* 9. Read Only Notice */}
              <div className="alert alert-success bg-success-transparent border-success d-flex align-items-center p-3 mt-4 mb-0">
                <i className="ti ti-info-circle fs-20 me-2 text-success"></i>
                <span className="text-dark fs-14">This is a submitted application. You can view details and track approval status.</span>
              </div>
            </>
          );
        })()}

        {/* Enhanced Chat Drawer - Corner Popup Style */}
        {showChat && (
          <>
            {/* Soft click-away backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100"
              style={{ zIndex: 1055, backgroundColor: "rgba(15, 23, 42, 0.2)", backdropFilter: "blur(2px)" }}
              onClick={handleChatToggle}
            />

            {/* Chat Widget Docked to Bottom-Right Corner */}
            <div
              className="position-fixed bg-white chat-corner-widget shadow-lg d-flex flex-column"
              style={{
                zIndex: 1060,
              }}
            >
              {/* Chat Header */}
              <div className="d-flex align-items-center justify-content-between px-3.5 py-3 border-bottom bg-white flex-shrink-0 chat-widget-header">
                <div className="d-flex align-items-center gap-2.5">
                  <div className="position-relative flex-shrink-0">
                    <div
                      className="avatar rounded-2 d-flex align-items-center justify-content-center fw-bold shadow-xs"
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "6px",
                        background: "linear-gradient(135deg, #f26522 0%, #d94e0c 100%)",
                        color: "#fff",
                      }}
                    >
                      <i className="ti ti-messages fs-18"></i>
                    </div>
                    <span
                      className="position-absolute bottom-0 end-0 bg-success border border-2 border-white"
                      style={{ width: "9px", height: "9px", borderRadius: "2px" }}
                      title="Active"
                    />
                  </div>
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0 fw-bold text-dark fs-14">Chat - Outside Job</h6>
                      <span className="badge bg-success-subtle text-success fs-10 px-2 py-0.5 rounded-1 fw-semibold">
                        Active
                      </span>
                    </div>
                    <small className="text-muted fs-11 d-block text-truncate" style={{ maxWidth: "230px" }}>
                      Ref: #{applicationData.referenceNumber || applicationData.id}
                      {applicationData.empID ? ` • Emp: ${applicationData.empID}` : ""}
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-1.5">
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={() =>
                      dispatch(
                        fetchMessages({ nocRequestId: applicationData.id })
                      )
                    }
                    disabled={chatLoading}
                    title="Refresh Messages"
                  >
                    <i className={`ti ti-refresh fs-14 ${chatLoading ? "ti-spin text-primary" : ""}`}></i>
                  </button>
                  <button
                    className="btn btn-icon btn-sm btn-light rounded-2 text-muted chat-header-btn"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                    onClick={handleChatToggle}
                    title="Close Chat"
                  >
                    <i className="ti ti-x fs-16"></i>
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="chat-messages-container p-3 flex-grow-1 overflow-auto" style={{ backgroundColor: "#f8fafc" }}>
                {(chatError || uploadError) && (
                  <div className="alert alert-danger alert-dismissible py-2 px-3 fs-12 mb-3 rounded-2 shadow-xs">
                    <button
                      type="button"
                      className="btn-close fs-10"
                      onClick={() => {
                        dispatch(clearError());
                        dispatch(clearUploadError());
                      }}
                    ></button>
                    {chatError || uploadError}
                  </div>
                )}

                {chatLoading && messages.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted small mt-2 fs-12">Loading conversation...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-5 my-2 text-muted">
                    <div
                      className="avatar avatar-md bg-white rounded-2 shadow-xs mx-auto mb-2 d-flex align-items-center justify-content-center text-primary"
                      style={{ width: 44, height: 44, borderRadius: "6px" }}
                    >
                      <i className="ti ti-messages fs-20"></i>
                    </div>
                    <h6 className="fw-semibold text-dark fs-13 mb-1">No Messages Yet</h6>
                    <p className="small text-muted mb-0 fs-11">Send a message below to start the conversation.</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-center my-2.5">
                      <span className="badge bg-white text-muted border shadow-xs px-3 py-1 rounded-1 fs-10 fw-medium">
                        Today
                      </span>
                    </div>
                    {messages.map((msg: ChatMessage, index: number) => {
                      const isOwn = isMyMessage(msg);
                      const isAttachmentMsg = Boolean(msg.message && msg.message.startsWith("📎"));
                      const displayMsgText = isAttachmentMsg ? "" : msg.message;
                      const attachmentsList: any[] =
                        msg.documents && msg.documents.length > 0
                          ? msg.documents
                          : msg.attachments && msg.attachments.length > 0
                            ? msg.attachments
                            : isAttachmentMsg
                              ? [msg.message.replace(/^[📎\s]+/, "").trim()]
                              : [];

                      return (
                        <div
                          key={msg.id || index}
                          className={`d-flex mb-3.5 ${isOwn ? "justify-content-end" : "justify-content-start"}`}
                        >
                          {/* Approver/Participant Avatar */}
                          {!isOwn && (
                            <div className="me-2 flex-shrink-0 align-self-end mb-0.5">
                              <EmployeeAvatar
                                empName={msg.senderName || "Approver"}
                                empImage={msg.senderImage}
                                empId={msg.senderEmpId}
                                size="sm"
                                shape="rounded"
                                showName={false}
                                showEmpId={false}
                                className="rounded-2 shadow-xs"
                              />
                            </div>
                          )}

                          <div
                            className="position-relative"
                            style={{ maxWidth: "84%", wordBreak: "break-word" }}
                          >
                            {/* Sender Label Above Bubble */}
                            <div
                              className={`d-flex align-items-center gap-1.5 mb-1 px-1 ${isOwn ? "justify-content-end" : "justify-content-start"
                                }`}
                            >
                              <span className="fw-semibold text-dark fs-11">
                                {isOwn ? "You" : (msg.senderName || "Approver")}
                              </span>
                              <span
                                className={`badge fs-9 px-1.5 py-0.5 text-uppercase rounded-1 fw-semibold ${isOwn
                                    ? "bg-primary-subtle text-primary"
                                    : msg.senderType === "approver"
                                      ? "bg-warning-subtle text-warning-emphasis"
                                      : "bg-secondary-subtle text-secondary"
                                  }`}
                              >
                                {msg.senderType || (isOwn ? "employee" : "approver")}
                              </span>
                            </div>

                            <div
                              className={`p-3 shadow-xs position-relative ${isOwn
                                  ? "chat-bubble-outgoing text-white"
                                  : "chat-bubble-incoming text-dark bg-white border"
                                }`}
                              style={{
                                minWidth: "120px",
                                borderRadius: "6px",
                              }}
                            >
                              {msg.documentType && (
                                <div className="mb-2">
                                  <span
                                    className={`badge fs-10 fw-medium px-2 py-1 rounded-1 ${isOwn
                                        ? "bg-white bg-opacity-25 text-white"
                                        : "bg-warning-subtle text-warning-emphasis border border-warning"
                                      }`}
                                  >
                                    <i className="ti ti-file-certificate me-1"></i>
                                    Requested: {msg.documentType}
                                  </span>
                                </div>
                              )}

                              {displayMsgText && (
                                <p
                                  className="mb-0 chat-message-content"
                                  style={{
                                    fontSize: "13.5px",
                                    lineHeight: "1.5",
                                    color: isOwn ? "#ffffff" : "#1e293b",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {displayMsgText}
                                </p>
                              )}

                              {/* Render Interactive Attachments / Images */}
                              {attachmentsList.length > 0 && (
                                <div
                                  className={`d-flex flex-column gap-2 ${displayMsgText ? "mt-2.5 pt-2 border-top" : ""
                                    }`}
                                  style={{
                                    borderColor: isOwn ? "rgba(255, 255, 255, 0.25)" : "#f1f5f9",
                                  }}
                                >
                                  {attachmentsList.map((attachment: any, attIdx: number) => {
                                    const rawName =
                                      typeof attachment === "string"
                                        ? attachment
                                        : attachment.fileName ||
                                        attachment.name ||
                                        attachment.FileName ||
                                        "Attachment";
                                    const fileName =
                                      rawName.split("/").pop()?.split("-").slice(2).join("-") ||
                                      rawName.split("/").pop() ||
                                      "Attachment";
                                    const directUrl =
                                      typeof attachment === "object"
                                        ? attachment.url
                                        : rawName.startsWith("http")
                                          ? rawName
                                          : undefined;
                                    const isImg =
                                      /\.(jpg|jpeg|png|webp|gif)$/i.test(fileName) ||
                                      (typeof attachment === "object" &&
                                        attachment.contentType?.startsWith("image/"));
                                    const isPdf =
                                      /\.pdf$/i.test(fileName) ||
                                      (typeof attachment === "object" &&
                                        attachment.contentType === "application/pdf");
                                    const fileSize =
                                      typeof attachment === "object" ? attachment.fileSize : undefined;

                                    return (
                                      <div key={attIdx} className="d-flex flex-column gap-1">
                                        {/* Inline image preview if direct url exists */}
                                        {isImg && directUrl && (
                                          <div
                                            className="overflow-hidden rounded-2 border mb-1 cursor-pointer position-relative shadow-xs"
                                            style={{
                                              maxHeight: "190px",
                                              maxWidth: "280px",
                                              borderRadius: "6px",
                                              backgroundColor: "#000",
                                            }}
                                            onClick={() => handleAttachmentClick(attachment)}
                                            title="Click to view full image"
                                          >
                                            <img
                                              src={directUrl}
                                              alt={fileName}
                                              className="w-100 h-100 object-fit-cover"
                                              style={{
                                                maxHeight: "190px",
                                                display: "block",
                                                cursor: "pointer",
                                              }}
                                            />
                                          </div>
                                        )}

                                        {/* Attachment Card */}
                                        <div
                                          className="d-flex align-items-center justify-content-between rounded-1 shadow-xs attachment-card cursor-pointer"
                                          onClick={() => handleAttachmentClick(attachment)}
                                          style={{
                                            cursor: "pointer",
                                            borderRadius: "5px",
                                            transition: "all 0.15s ease",
                                            backgroundColor: "#ffffff",
                                            border: "1px solid #e2e8f0",
                                            padding: "4px 6px",
                                            maxWidth: "235px",
                                          }}
                                          title="Click to view / open attachment"
                                        >
                                          <div className="d-flex align-items-center overflow-hidden me-1.5" style={{ minWidth: 0 }}>
                                            <div
                                              className="rounded-1 d-flex align-items-center justify-content-center flex-shrink-0 me-1.5"
                                              style={{
                                                width: "24px",
                                                height: "24px",
                                                borderRadius: "4px",
                                                backgroundColor: isPdf
                                                  ? "#fee2e2"
                                                  : isImg
                                                    ? "#dbeafe"
                                                    : "#f1f5f9",
                                                color: isPdf
                                                  ? "#dc2626"
                                                  : isImg
                                                    ? "#2563eb"
                                                    : "#475569",
                                              }}
                                            >
                                              <i className={`${isImg ? "ti ti-photo" : isPdf ? "ti ti-file-text" : "ti ti-paperclip"} fs-13`}></i>
                                            </div>
                                            <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                              <span
                                                className="text-truncate fw-semibold d-block text-decoration-underline"
                                                style={{
                                                  color: "#0f172a",
                                                  fontSize: "11px",
                                                  maxWidth: "145px",
                                                  lineHeight: "1.2",
                                                }}
                                              >
                                                {fileName}
                                              </span>
                                              <small
                                                className="d-block text-truncate"
                                                style={{
                                                  fontSize: "9px",
                                                  color: "#64748b",
                                                  lineHeight: "1.1",
                                                  marginTop: "1px",
                                                }}
                                              >
                                                {fileSize ? `${fileSize} • ` : ""}
                                                {isImg ? "Image" : isPdf ? "PDF" : "File"}
                                              </small>
                                            </div>
                                          </div>
                                          <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-icon rounded-1 p-0 d-flex align-items-center justify-content-center"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleAttachmentClick(attachment);
                                              }}
                                              style={{
                                                width: "21px",
                                                height: "21px",
                                                borderRadius: "3px",
                                                color: "#334155",
                                                border: "1px solid #e2e8f0",
                                                background: "#f8fafc",
                                              }}
                                              title="Open in External Tab"
                                            >
                                              <i className="ti ti-external-link" style={{ fontSize: "11px" }}></i>
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-icon rounded-1 p-0 d-flex align-items-center justify-content-center"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                downloadChatAttachment(attachment, applicationData?.id, "OUTSIDE_JOB", dispatch, attachments);
                                              }}
                                              style={{
                                                width: "21px",
                                                height: "21px",
                                                borderRadius: "3px",
                                                color: "#334155",
                                                border: "1px solid #e2e8f0",
                                                background: "#f8fafc",
                                              }}
                                              title="Download Attachment"
                                            >
                                              <i className="ti ti-download" style={{ fontSize: "11px" }}></i>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Timestamp */}
                              <div className="d-flex align-items-center justify-content-end gap-1.5 mt-1.5">
                                <small
                                  style={{
                                    fontSize: "10px",
                                    color: isOwn ? "rgba(255, 255, 255, 0.85)" : "#94a3b8",
                                  }}
                                >
                                  {formatMessageTime(msg.createdAt)}
                                </small>
                                {isOwn && <i className="ti ti-checks fs-11 text-white-50"></i>}
                              </div>
                            </div>
                          </div>

                          {/* Employee Avatar */}
                          {isOwn && (
                            <div className="ms-2 flex-shrink-0 align-self-end mb-0.5">
                              <EmployeeAvatar
                                empName={
                                  msg.senderName ||
                                  applicationData.employeeName ||
                                  "User"
                                }
                                empImage={msg.senderImage || (applicationData as any).employeeDetails?.image}
                                empId={msg.senderEmpId || applicationData.empID}
                                size="sm"
                                shape="rounded"
                                showName={false}
                                showEmpId={false}
                                className="rounded-2 shadow-xs"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input */}
              <div className="chat-input-container bg-white border-top p-3 flex-shrink-0">
                {/* File Attachments Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mb-2.5 file-attachment-preview">
                    <div className="d-flex flex-wrap gap-1.5">
                      {selectedFiles.map((file, index) => {
                        const fileSizeKB = (file.size / 1024).toFixed(1);
                        const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name);
                        const isPdf = /\.pdf$/i.test(file.name);

                        return (
                          <div
                            key={index}
                            className="d-flex align-items-center bg-light border rounded-1 px-2.5 py-1 shadow-xs"
                            style={{ borderRadius: "4px" }}
                          >
                            <i
                              className={`${isImg
                                  ? "ti ti-photo text-info"
                                  : isPdf
                                    ? "ti ti-file-type-pdf text-danger"
                                    : "ti ti-file-text text-primary"
                                } me-1.5 fs-13`}
                            ></i>
                            <span
                              className="small text-truncate fw-medium text-dark me-1.5 fs-11"
                              style={{ maxWidth: "160px" }}
                            >
                              {file.name}
                            </span>
                            <span className="text-muted fs-10 me-1.5">({fileSizeKB}KB)</span>
                            <button
                              type="button"
                              className="btn btn-xs btn-danger rounded-1 p-0 d-flex align-items-center justify-content-center"
                              onClick={() => removeFile(index)}
                              title="Remove file"
                              style={{ width: "18px", height: "18px", borderRadius: "3px" }}
                            >
                              <i className="ti ti-x fs-9"></i>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="d-flex align-items-center gap-2">
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelection}
                    multiple
                    className="d-none"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />

                  {/* Attachment Button */}
                  <button
                    type="button"
                    className="btn btn-light rounded-2 d-flex align-items-center justify-content-center flex-shrink-0 chat-attach-btn border"
                    onClick={triggerFileInput}
                    disabled={chatLoading || uploading}
                    style={{ width: "38px", height: "38px", borderRadius: "6px" }}
                    title="Attach files (PDF, Doc, Image)"
                  >
                    {uploading ? (
                      <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14 }}>
                        <span className="visually-hidden">...</span>
                      </div>
                    ) : (
                      <i className="ti ti-paperclip fs-17 text-muted"></i>
                    )}
                  </button>

                  <div className="flex-grow-1 position-relative">
                    <input
                      type="text"
                      className="form-control rounded-2 px-3 py-2 fs-13 border chat-input-field"
                      placeholder={selectedFiles.length > 0 ? "Type a message for your attachment (required)..." : "Type your message... (Press Enter to send)"}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={chatLoading || uploading}
                      style={{
                        backgroundColor: "#f8fafc",
                        borderColor: "#cbd5e1",
                        borderRadius: "6px",
                        height: "38px",
                      }}
                    />
                  </div>

                  <button
                    className="btn btn-primary rounded-2 d-flex align-items-center justify-content-center flex-shrink-0 shadow chat-send-btn"
                    onClick={handleSendMessage}
                    disabled={chatLoading || uploading || (!newMessage.trim() && selectedFiles.length === 0)}
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "6px",
                      background: "linear-gradient(135deg, #f26522 0%, #d94e0c 100%)",
                      border: "none",
                    }}
                    title="Send message"
                  >
                    {chatLoading || uploading ? (
                      <div className="spinner-border spinner-border-sm text-white" role="status" style={{ width: 14, height: 14 }}>
                        <span className="visually-hidden">...</span>
                      </div>
                    ) : (
                      <i className="ti ti-send text-white fs-15"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Media / Lightbox Preview Modal */}
        {previewMedia && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center"
            style={{ zIndex: 1080, backdropFilter: "blur(4px)" }}
            onClick={() => setPreviewMedia(null)}
          >
            <div
              className="bg-white rounded-2 shadow-lg p-3.5 position-relative"
              style={{ maxWidth: "90vw", maxHeight: "90vh", minWidth: "320px", borderRadius: "8px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                <h6 className="mb-0 fw-bold text-dark text-truncate fs-14" style={{ maxWidth: "70%" }}>
                  <i className="ti ti-photo me-1.5 text-primary"></i>
                  {previewMedia.title}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <a
                    href={previewMedia.url}
                    target="_blank"
                    rel="noreferrer"
                    download={previewMedia.title}
                    className="btn btn-sm btn-outline-primary py-1 px-2.5 fs-12 rounded-2"
                    style={{ borderRadius: "6px" }}
                  >
                    <i className="ti ti-download me-1"></i>Download
                  </a>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setPreviewMedia(null)}
                  ></button>
                </div>
              </div>
              <div className="text-center overflow-auto p-2" style={{ maxHeight: "calc(90vh - 100px)" }}>
                {previewMedia.isImage ? (
                  <img
                    src={previewMedia.url}
                    alt={previewMedia.title}
                    className="img-fluid rounded-2"
                    style={{ maxHeight: "75vh", objectFit: "contain", borderRadius: "6px" }}
                  />
                ) : (
                  <iframe
                    src={previewMedia.url}
                    title={previewMedia.title}
                    style={{ width: "80vw", height: "70vh", border: "none" }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Document Type Selection Dialog */}
        {showDocumentTypeDialog && (
          <>
            {/* Backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
              style={{ zIndex: 1065, backdropFilter: "blur(2px)" }}
              onClick={handleCancelDocumentType}
            />

            {/* Dialog */}
            <div
              className="position-fixed top-50 start-50 translate-middle bg-white rounded-2 shadow-lg"
              style={{ zIndex: 1070, minWidth: "400px", maxWidth: "90vw", borderRadius: "8px" }}
            >
              <div className="p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 className="mb-0 fw-bold text-dark">
                    <i className="ti ti-file-text me-2 text-primary"></i>
                    Select Document Type
                  </h5>
                  <button
                    className="btn-close"
                    onClick={handleCancelDocumentType}
                  ></button>
                </div>

                <p className="text-muted mb-3 fs-13">
                  Which type of document are you requesting?
                </p>

                <div className="mb-3">
                  <div className="form-check mb-2.5 p-3 bg-light rounded-2 border" style={{ borderRadius: "6px" }}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="documentType"
                      id="docTypeEducation"
                      value="Education"
                      checked={selectedDocumentType === "Education"}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="docTypeEducation"
                    >
                      <span className="fw-semibold text-dark d-block">Education Documents</span>
                      <small className="text-muted">Certificates, degrees, marksheets</small>
                    </label>
                  </div>

                  <div className="form-check mb-2.5 p-3 bg-light rounded-2 border" style={{ borderRadius: "6px" }}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="documentType"
                      id="docTypeAdvertisement"
                      value="Advertisement"
                      checked={selectedDocumentType === "Advertisement"}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                    />
                    <label
                      className="form-check-label ms-2"
                      htmlFor="docTypeAdvertisement"
                    >
                      <span className="fw-semibold text-dark d-block">Advertisement Documents</span>
                      <small className="text-muted">Job vacancy circulars, clippings</small>
                    </label>
                  </div>

                  <div className="form-check mb-2.5 p-3 bg-light rounded-2 border" style={{ borderRadius: "6px" }}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="documentType"
                      id="docTypeOthers"
                      value="Others"
                      checked={selectedDocumentType === "Others"}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                    />
                    <label className="form-check-label ms-2" htmlFor="docTypeOthers">
                      <span className="fw-semibold text-dark d-block">Other Documents</span>
                      <small className="text-muted">Any other supporting files</small>
                    </label>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-light rounded-2"
                    style={{ borderRadius: "6px" }}
                    onClick={handleCancelDocumentType}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary rounded-2"
                    style={{ borderRadius: "6px" }}
                    onClick={handleDocumentTypeSelection}
                    disabled={!selectedDocumentType}
                  >
                    <i className="ti ti-check me-1"></i>
                    Send Request
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <style>
          {`
            @keyframes chatSlideIn {
              from {
                transform: translateY(16px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            
            .chat-corner-widget {
              bottom: 24px;
              right: 28px;
              width: 440px;
              max-width: calc(100vw - 32px);
              height: 600px;
              max-height: calc(100vh - 48px);
              border-radius: 8px;
              border: 1px solid #cbd5e1;
              box-shadow: 0 16px 36px -6px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(0, 0, 0, 0.05);
              overflow: hidden;
              animation: chatSlideIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
            }
            
            @media (max-width: 576px) {
              .chat-corner-widget {
                bottom: 0 !important;
                right: 0 !important;
                left: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                height: 85vh !important;
                max-height: 85vh !important;
                border-radius: 8px 8px 0 0 !important;
              }
            }
            
            .chat-bubble-incoming {
              border-radius: 6px !important;
              border: 1px solid #e2e8f0;
              background-color: #ffffff;
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
            }

            .chat-bubble-outgoing {
              border-radius: 6px !important;
              background: linear-gradient(135deg, #f26522 0%, #d94e0c 100%);
              box-shadow: 0 2px 8px rgba(242, 101, 34, 0.22);
            }

            .chat-header-btn:hover {
              background-color: #f1f5f9 !important;
              color: #0f172a !important;
            }

            .chat-attach-btn:hover {
              background-color: #e2e8f0 !important;
            }

            .chat-send-btn:hover:not(:disabled) {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(242, 101, 34, 0.35) !important;
            }

            .chat-input-field:focus {
              background-color: #ffffff !important;
              border-color: #f26522 !important;
              box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.15) !important;
            }

            .chat-messages-container {
              flex: 1;
              overflow-y: auto;
              min-height: 0;
            }
            
            .chat-input-container {
              flex-shrink: 0;
            }
            
            .chat-att-card-in:hover {
              background-color: #f1f5f9 !important;
              border-color: #cbd5e1 !important;
            }

            .chat-att-card-out:hover {
              background-color: rgba(255, 255, 255, 0.26) !important;
              border-color: rgba(255, 255, 255, 0.38) !important;
            }

            .chat-message-content {
              word-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .file-attachment-preview {
              max-width: 100%;
              overflow-x: auto;
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default NOCOutsideJobDetails;
